// AdminProyectos.js — Fase 9: Gestión de Proyectos Multi-Tenant
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { proyectoService, consultaService, operatixService, convenioService, BACKEND_BASE } from '../../services/api';
import axios from 'axios';
import { API_BASE_URL } from '../../services/api';
import AdminLandingEditor from './AdminLandingEditor';
import AdminCertificadoBuilder from './AdminCertificadoBuilder';
import {
  ADMIN_COLORS,
  AdminContainer as Container,
  AdminTopBar as TopBar,
  AdminTitle as Title,
  AdminButton,
  AdminActionBtn,
  AdminTable,
  AdminBadge,
  AdminModalOverlay as Modal,
  AdminModalContent as ModalCard,
  AdminModalTitle as ModalTitle,
  AdminModalActions as BtnRow,
  AdminFormGroup as FormGroup,
  AdminFormGrid as FormGrid,
  AdminLabel as Label,
  AdminInput as Input,
  AdminTextarea as TextArea,
  AdminSelect as Select,
  AdminLoading
} from './AdminShared';

// Meta App ID de Operatix (aprobada por Meta)
const META_APP_ID = '1235794175100517';
const META_CONFIG_ID = '802318412947494';

// ============================================================
// STYLED COMPONENTS (unique to this component)
// ============================================================

const AddBtn = styled(AdminButton)`
  background: linear-gradient(135deg, ${ADMIN_COLORS.primary}, ${ADMIN_COLORS.accent});
  &:hover { box-shadow: 0 4px 12px rgba(20,182,203,0.3); }
`;

const Table = styled(AdminTable)`
  th, td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid ${ADMIN_COLORS.borderLight};
    font-size: 0.9rem;
  }
  th {
    background: ${ADMIN_COLORS.bg};
    color: #475569;
    font-weight: 600;
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  tr:hover td { background: ${ADMIN_COLORS.bg}; }
`;

const Badge = styled(AdminBadge)`
  background: ${p => p.$active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)'};
  color: ${p => p.$active ? ADMIN_COLORS.success : ADMIN_COLORS.danger};
`;

const ActionBtn = styled(AdminActionBtn)`
  &:hover { border-color: ${ADMIN_COLORS.accent}; color: ${ADMIN_COLORS.accent}; }
`;

const DeleteBtn = styled(ActionBtn)`
  &:hover { border-color: #ef4444; color: #ef4444; }
`;

const LandingBtn = styled(ActionBtn)`
  &:hover { border-color: #8b5cf6; color: #8b5cf6; }
`;

const WhatsAppBtn = styled(ActionBtn)`
  &:hover { border-color: #25d366; color: #25d366; }
`;

const MembersBtn = styled(ActionBtn)`
  &:hover { border-color: #3b82f6; color: #3b82f6; }
`;

const RegistroBtn = styled(ActionBtn)`
  &:hover { border-color: #f59e0b; color: #f59e0b; }
`;

const CertBtn = styled(ActionBtn)`
  &:hover { border-color: #059669; color: #059669; }
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  background: ${ADMIN_COLORS.bg};
  border: 1px solid ${ADMIN_COLORS.borderLight};
  border-radius: 16px;
  font-size: 0.82rem;

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: ${ADMIN_COLORS.danger};
    font-size: 0.9rem;
    line-height: 1;
    padding: 0;
    &:hover { color: #dc2626; }
  }
`;

const InlineRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const WaPanel = styled.div`
  position: fixed;
  top: 0; right: 0; bottom: 0;
  width: 420px;
  max-width: 95vw;
  background: #fff;
  box-shadow: -4px 0 30px rgba(0,0,0,0.15);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const WaPanelHeader = styled.div`
  padding: 1.5rem;
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WaPanelBody = styled.div`
  padding: 1.5rem;
  flex: 1;
`;

const WaStatusCard = styled.div`
  background: ${p => p.$connected ? '#f0fdf4' : '#fef2f2'};
  border: 1px solid ${p => p.$connected ? '#bbf7d0' : '#fecaca'};
  border-radius: 12px;
  padding: 1.2rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const WaConnectBtn = styled.button`
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #fff;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 15px rgba(37,211,102,0.4); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const WaDisconnectBtn = styled.button`
  background: none;
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  margin-top: 1rem;
  &:hover { background: #fef2f2; }
`;

const WaOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
`;

const SlugLink = styled.a`
  color: ${ADMIN_COLORS.accent};
  text-decoration: none;
  font-size: 0.85rem;
  &:hover { text-decoration: underline; }
`;

const ColorInput = styled.input`
  width: 50px;
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  padding: 2px;
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CheckboxList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.3rem 0.4rem;
  border-radius: 6px;
  transition: background 0.15s;
  &:hover { background: ${ADMIN_COLORS.bgHover}; }
`;

const SaveBtn = styled(AdminButton)`
  background: linear-gradient(135deg, ${ADMIN_COLORS.primary}, ${ADMIN_COLORS.accent});
  padding: 0.6rem 1.5rem;
`;

const CancelBtn = styled(AdminButton)`
  background: ${ADMIN_COLORS.bgHover};
  color: #475569;
`;

const LogoPreview = styled.img`
  height: 48px;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  padding: 4px;
  background: ${ADMIN_COLORS.bg};
`;

const ToggleSwitch = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;

  input { display: none; }
  .slider {
    width: 40px; height: 22px;
    background: ${p => p.$on ? ADMIN_COLORS.accent : '#cbd5e1'};
    border-radius: 11px;
    position: relative;
    transition: background 0.2s;
    &::after {
      content: '';
      position: absolute;
      width: 18px; height: 18px;
      border-radius: 50%;
      background: #fff;
      top: 2px;
      left: ${p => p.$on ? '20px' : '2px'};
      transition: left 0.2s;
    }
  }
`;

const AdminProyectos = () => {
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [landingProyecto, setLandingProyecto] = useState(null);
  const [certProyecto, setCertProyecto] = useState(null);
  const [waProyecto, setWaProyecto] = useState(null); // proyecto seleccionado para WhatsApp panel
  const [waStatus, setWaStatus] = useState(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waConnecting, setWaConnecting] = useState(false);

  // Registro panel state
  const [registroProyecto, setRegistroProyecto] = useState(null);
  const [convenios, setConvenios] = useState([]);
  const [registroConfig, setRegistroConfig] = useState({
    campos: {
      convenio: { visible: false, requerido: false },
      telefono: { visible: false, requerido: false },
      especialidad: { visible: false, requerido: false },
      pais: { visible: false, requerido: false },
      dni: { visible: false, requerido: false, etiqueta: 'DNI' }
    },
    especialidades: [],
    paises: ['Colombia', 'México', 'Perú', 'Ecuador', 'Chile', 'Argentina']
  });
  const [registroLoading, setRegistroLoading] = useState(false);
  const [registroSaving, setRegistroSaving] = useState(false);
  const [newConvenio, setNewConvenio] = useState({ nombre: '', institucion: '' });
  const [editingConvenio, setEditingConvenio] = useState(null);
  const [newEspecialidad, setNewEspecialidad] = useState('');
  const [newPais, setNewPais] = useState('');

  // Miembros panel state
  const [membersProyecto, setMembersProyecto] = useState(null);
  const [miembros, setMiembros] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addRol, setAddRol] = useState('supervisor');

  // Form state
  const [form, setForm] = useState({
    nombre: '', slug: '', dominio_personalizado: '',
    color_primario: '#0f355b', color_secundario: '#14b6cb',
    titulo_landing: '', subtitulo_landing: '',
    rol_default: '', registro_abierto: 1, activo: 1,
    documento_ids: [],
    modos_habilitados: { consulta: true, mentor: true, evaluacion: true, reto: true, practica: false }
  });
  const [logoFile, setLogoFile] = useState(null);

  // Documentos y grupos para los selectors
  const [documentos, setDocumentos] = useState([]);
  const [groups, setGroups] = useState([]);

  const fetchProyectos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await proyectoService.list();
      setProyectos(res.data?.proyectos || []);
    } catch (err) {
      toast.error('Error cargando proyectos');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAuxData = useCallback(async () => {
    try {
      const [docsRes, groupsRes] = await Promise.all([
        consultaService.getDocumentos(),
        axios.get(`${API_BASE_URL}/admin/content-groups.php`)
      ]);
      setDocumentos(docsRes.data?.documentos || docsRes.data || []);
      setGroups(groupsRes.data?.groups || []);
    } catch (err) {
      console.error('Error cargando datos auxiliares:', err);
    }
  }, []);

  useEffect(() => { fetchProyectos(); fetchAuxData(); }, [fetchProyectos, fetchAuxData]);

  const resetForm = () => {
    setForm({
      nombre: '', slug: '', dominio_personalizado: '',
      color_primario: '#0f355b', color_secundario: '#14b6cb',
      titulo_landing: '', subtitulo_landing: '',
      rol_default: '', registro_abierto: 1, activo: 1,
      documento_ids: [],
      modos_habilitados: { consulta: true, mentor: true, evaluacion: true, reto: true, practica: false }
    });
    setLogoFile(null);
    setEditing(null);
  };

  const openCreate = () => { resetForm(); setShowModal(true); };

  const openEdit = async (p) => {
    try {
      const res = await proyectoService.get(p.id);
      const pData = res.data?.proyecto;
      const configJson = typeof pData.config_json === 'string' ? JSON.parse(pData.config_json || '{}') : (pData.config_json || {});
      const modos = configJson.modos_habilitados || {};
      setForm({
        nombre: pData.nombre || '',
        slug: pData.slug || '',
        dominio_personalizado: pData.dominio_personalizado || '',
        color_primario: pData.color_primario || '#0f355b',
        color_secundario: pData.color_secundario || '#14b6cb',
        titulo_landing: pData.titulo_landing || '',
        subtitulo_landing: pData.subtitulo_landing || '',
        rol_default: pData.rol_default || '',
        registro_abierto: pData.registro_abierto ? 1 : 0,
        activo: pData.activo ? 1 : 0,
        documento_ids: pData.documento_ids || [],
        modos_habilitados: {
          consulta: modos.consulta !== false,
          mentor: modos.mentor !== false,
          evaluacion: modos.evaluacion !== false,
          reto: modos.reto !== false,
          practica: modos.practica === true
        }
      });
      setEditing(pData);
      setLogoFile(null);
      setShowModal(true);
    } catch (err) {
      toast.error('Error cargando proyecto');
    }
  };

  const handleSave = async () => {
    if (!form.nombre || !form.slug) {
      toast.error('Nombre y slug son requeridos');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      if (editing) fd.append('id', editing.id);
      fd.append('nombre', form.nombre);
      fd.append('slug', form.slug);
      fd.append('dominio_personalizado', form.dominio_personalizado);
      fd.append('color_primario', form.color_primario);
      fd.append('color_secundario', form.color_secundario);
      fd.append('titulo_landing', form.titulo_landing);
      fd.append('subtitulo_landing', form.subtitulo_landing);
      fd.append('rol_default', form.rol_default);
      fd.append('registro_abierto', form.registro_abierto);
      fd.append('activo', form.activo);
      fd.append('documento_ids', JSON.stringify(form.documento_ids));
      if (logoFile) fd.append('logo', logoFile);

      if (editing) {
        await proyectoService.update(fd);
        // Guardar modos_habilitados en config_json
        await proyectoService.updateJSON({
          id: editing.id,
          config_json: { modos_habilitados: form.modos_habilitados }
        });
        toast.success('Proyecto actualizado');
      } else {
        const createRes = await proyectoService.create(fd);
        const newId = createRes.data?.id;
        if (newId) {
          await proyectoService.updateJSON({
            id: newId,
            config_json: { modos_habilitados: form.modos_habilitados }
          });
        }
        toast.success('Proyecto creado');
      }
      setShowModal(false);
      fetchProyectos();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error guardando proyecto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`¿Desactivar el proyecto "${p.nombre}"?`)) return;
    try {
      await proyectoService.delete(p.id);
      toast.success('Proyecto desactivado');
      fetchProyectos();
    } catch (err) {
      toast.error('Error desactivando proyecto');
    }
  };

  const openLanding = async (p) => {
    try {
      const res = await proyectoService.get(p.id);
      setLandingProyecto(res.data?.proyecto || p);
    } catch (err) {
      toast.error('Error cargando proyecto para landing');
    }
  };

  const openCert = async (p) => {
    try {
      const res = await proyectoService.get(p.id);
      setCertProyecto(res.data?.proyecto || p);
    } catch (err) {
      toast.error('Error cargando proyecto para certificado');
    }
  };

  const autoSlug = (name) => {
    return name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // === WhatsApp Panel handlers ===
  const openWhatsApp = async (p) => {
    setWaProyecto(p);
    setWaLoading(true);
    try {
      const res = await operatixService.whatsappStatus(p.id);
      setWaStatus(res.data?.whatsapp || { connected: false });
    } catch (err) {
      setWaStatus({ connected: false });
    } finally {
      setWaLoading(false);
    }
  };

  const closeWhatsApp = () => {
    setWaProyecto(null);
    setWaStatus(null);
  };

  const loadFBSDK = () => {
    return new Promise((resolve) => {
      if (window.FB) { resolve(); return; }
      window.fbAsyncInit = function() {
        window.FB.init({ appId: META_APP_ID, cookie: true, xfbml: false, version: 'v21.0' });
        resolve();
      };
      if (!document.getElementById('facebook-jssdk')) {
        const js = document.createElement('script');
        js.id = 'facebook-jssdk';
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        document.body.appendChild(js);
      }
    });
  };

  const handleConnectWhatsApp = async () => {
    setWaConnecting(true);
    try {
      await loadFBSDK();
      window.FB.login(function(response) {
        if (response.authResponse && response.authResponse.code) {
          processWhatsAppCode(response.authResponse.code);
        } else {
          toast.error('Proceso cancelado o no autorizado');
          setWaConnecting(false);
        }
      }, {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' }
      });
    } catch (err) {
      toast.error('Error cargando Facebook SDK');
      setWaConnecting(false);
    }
  };

  const processWhatsAppCode = async (code) => {
    try {
      const res = await operatixService.connectWhatsApp(waProyecto.id, code);
      if (res.data?.success) {
        toast.success('WhatsApp conectado exitosamente');
        setWaStatus(res.data.whatsapp);
        fetchProyectos();
      } else {
        toast.error(res.data?.error || 'Error conectando WhatsApp');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error conectando WhatsApp');
    } finally {
      setWaConnecting(false);
    }
  };

  const handleDisconnectWhatsApp = async () => {
    if (!window.confirm('¿Desconectar WhatsApp de este proyecto? Los programas de entrenamiento activos dejarán de funcionar.')) return;
    try {
      await operatixService.disconnectWhatsApp(waProyecto.id);
      toast.success('WhatsApp desconectado');
      setWaStatus({ connected: false });
      fetchProyectos();
    } catch (err) {
      toast.error('Error desconectando');
    }
  };

  // === Miembros Panel handlers ===
  const openMembers = async (p) => {
    setMembersProyecto(p);
    setMembersLoading(true);
    setSearchQuery('');
    setSearchResults([]);
    try {
      const res = await proyectoService.getMiembros(p.id);
      setMiembros(res.data?.miembros || []);
    } catch (err) {
      toast.error('Error cargando miembros');
      setMiembros([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const closeMembers = () => {
    setMembersProyecto(null);
    setMiembros([]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchUsers = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const res = await proyectoService.buscarUsuarios(q);
      const results = (res.data?.usuarios || []).filter(
        u => !miembros.some(m => parseInt(m.user_id) === parseInt(u.id))
      );
      setSearchResults(results);
    } catch (err) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [miembros]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) handleSearchUsers(searchQuery);
      else setSearchResults([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearchUsers]);

  const handleAddMember = async (userId) => {
    try {
      await proyectoService.addMiembro({
        proyecto_id: membersProyecto.id,
        user_id: userId,
        rol_proyecto: addRol
      });
      toast.success('Miembro agregado');
      const res = await proyectoService.getMiembros(membersProyecto.id);
      setMiembros(res.data?.miembros || []);
      setSearchQuery('');
      setSearchResults([]);
      fetchProyectos();
    } catch (err) {
      toast.error('Error agregando miembro');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('¿Eliminar este miembro del proyecto?')) return;
    try {
      await proyectoService.removeMiembro(memberId);
      toast.success('Miembro eliminado');
      setMiembros(prev => prev.filter(m => m.id !== memberId));
      fetchProyectos();
    } catch (err) {
      toast.error('Error eliminando miembro');
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    const member = miembros.find(m => m.id === memberId);
    if (!member) return;
    try {
      await proyectoService.addMiembro({
        proyecto_id: membersProyecto.id,
        user_id: member.user_id,
        rol_proyecto: newRole
      });
      setMiembros(prev => prev.map(m =>
        m.id === memberId ? { ...m, rol_proyecto: newRole } : m
      ));
    } catch (err) {
      toast.error('Error actualizando rol');
    }
  };

  // === Registro Panel handlers ===
  const openRegistro = async (p) => {
    setRegistroProyecto(p);
    setRegistroLoading(true);
    try {
      const [convRes, projRes] = await Promise.all([
        convenioService.list(p.id),
        proyectoService.get(p.id)
      ]);
      setConvenios(convRes.data?.convenios || []);
      const cfg = projRes.data?.proyecto?.config_json;
      const parsed = typeof cfg === 'string' ? JSON.parse(cfg || '{}') : (cfg || {});
      if (parsed.registro_config) {
        setRegistroConfig(prev => ({
          ...prev,
          ...parsed.registro_config,
          campos: { ...prev.campos, ...(parsed.registro_config.campos || {}) }
        }));
      } else {
        setRegistroConfig({
          campos: {
            convenio: { visible: false, requerido: false },
            telefono: { visible: false, requerido: false },
            especialidad: { visible: false, requerido: false },
            pais: { visible: false, requerido: false },
            dni: { visible: false, requerido: false, etiqueta: 'DNI' }
          },
          especialidades: [],
          paises: ['Colombia', 'México', 'Perú', 'Ecuador', 'Chile', 'Argentina']
        });
      }
    } catch (err) {
      toast.error('Error cargando configuración de registro');
    } finally {
      setRegistroLoading(false);
    }
  };

  const closeRegistro = () => {
    setRegistroProyecto(null);
    setConvenios([]);
    setNewConvenio({ nombre: '', institucion: '' });
    setEditingConvenio(null);
  };

  const handleAddConvenio = async () => {
    if (!newConvenio.nombre.trim()) return;
    try {
      const res = await convenioService.create({ proyecto_id: registroProyecto.id, ...newConvenio });
      if (res.data?.success) {
        setConvenios(prev => [...prev, { id: res.data.id, ...newConvenio, activo: 1 }]);
        setNewConvenio({ nombre: '', institucion: '' });
        toast.success('Convenio creado');
      }
    } catch (err) {
      toast.error('Error creando convenio');
    }
  };

  const handleUpdateConvenio = async (conv) => {
    try {
      await convenioService.update(conv);
      setConvenios(prev => prev.map(c => c.id === conv.id ? { ...c, ...conv } : c));
      setEditingConvenio(null);
      toast.success('Convenio actualizado');
    } catch (err) {
      toast.error('Error actualizando convenio');
    }
  };

  const handleDeleteConvenio = async (id) => {
    if (!window.confirm('¿Eliminar este convenio?')) return;
    try {
      await convenioService.delete(id);
      setConvenios(prev => prev.filter(c => c.id !== id));
      toast.success('Convenio eliminado');
    } catch (err) {
      toast.error('Error eliminando convenio');
    }
  };

  const toggleCampo = (campo, key) => {
    setRegistroConfig(prev => ({
      ...prev,
      campos: {
        ...prev.campos,
        [campo]: { ...prev.campos[campo], [key]: !prev.campos[campo][key] }
      }
    }));
  };

  const handleSaveRegistroConfig = async () => {
    setRegistroSaving(true);
    try {
      await proyectoService.updateJSON({
        id: registroProyecto.id,
        config_json: { registro_config: registroConfig }
      });
      toast.success('Configuración de registro guardada');
    } catch (err) {
      toast.error('Error guardando configuración');
    } finally {
      setRegistroSaving(false);
    }
  };

  const addEspecialidad = () => {
    const val = newEspecialidad.trim();
    if (!val || registroConfig.especialidades.includes(val)) return;
    setRegistroConfig(prev => ({ ...prev, especialidades: [...prev.especialidades, val] }));
    setNewEspecialidad('');
  };

  const removeEspecialidad = (item) => {
    setRegistroConfig(prev => ({ ...prev, especialidades: prev.especialidades.filter(e => e !== item) }));
  };

  const addPais = () => {
    const val = newPais.trim();
    if (!val || registroConfig.paises.includes(val)) return;
    setRegistroConfig(prev => ({ ...prev, paises: [...prev.paises, val] }));
    setNewPais('');
  };

  const removePais = (item) => {
    setRegistroConfig(prev => ({ ...prev, paises: prev.paises.filter(p => p !== item) }));
  };

  const toggleDoc = (docId) => {
    setForm(prev => ({
      ...prev,
      documento_ids: prev.documento_ids.includes(docId)
        ? prev.documento_ids.filter(id => id !== docId)
        : [...prev.documento_ids, docId]
    }));
  };

  return (
    <Container>
      <TopBar>
        <Title>Proyectos Multi-Tenant</Title>
        <AddBtn onClick={openCreate}>+ Nuevo Proyecto</AddBtn>
      </TopBar>

      {loading ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Cargando proyectos...</p>
      ) : proyectos.length === 0 ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>No hay proyectos creados.</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Slug</th>
              <th>Dominio</th>
              <th>Rol Default</th>
              <th>Docs</th>
              <th>Miembros</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {proyectos.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 600 }}>
                  {p.logo && <LogoPreview src={`${BACKEND_BASE}/${p.logo}`} alt="" style={{ height: 24, marginRight: 8, verticalAlign: 'middle' }} />}
                  {p.nombre}
                </td>
                <td>
                  <SlugLink href={`https://${p.slug}.ateneomentoria.com`} target="_blank" rel="noopener noreferrer">
                    {p.slug}.ateneomentoria.com
                  </SlugLink>
                </td>
                <td style={{ fontSize: '0.85rem', color: '#64748b' }}>{p.dominio_personalizado || '—'}</td>
                <td><Badge $active={!!p.rol_default}>{p.rol_default || 'Ninguno'}</Badge></td>
                <td>{p.doc_count || 0}</td>
                <td>{p.member_count || 0}</td>
                <td><Badge $active={!!parseInt(p.activo)}>{parseInt(p.activo) ? 'Activo' : 'Inactivo'}</Badge></td>
                <td>
                  <ActionBtn onClick={() => openEdit(p)}>Editar</ActionBtn>
                  <MembersBtn onClick={() => openMembers(p)}>Miembros</MembersBtn>
                  <RegistroBtn onClick={() => openRegistro(p)}>Registro</RegistroBtn>
                  <LandingBtn onClick={() => openLanding(p)}>Landing</LandingBtn>
                  <CertBtn onClick={() => openCert(p)}>Certificado</CertBtn>
                  <WhatsAppBtn onClick={() => openWhatsApp(p)}>WhatsApp</WhatsAppBtn>
                  {parseInt(p.activo) === 1 && <DeleteBtn onClick={() => handleDelete(p)}>Desactivar</DeleteBtn>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {landingProyecto && (
        <AdminLandingEditor
          proyecto={landingProyecto}
          onClose={() => setLandingProyecto(null)}
          onSaved={() => { fetchProyectos(); }}
        />
      )}

      {certProyecto && (
        <AdminCertificadoBuilder
          proyecto={certProyecto}
          onBack={() => setCertProyecto(null)}
        />
      )}

      {waProyecto && (
        <>
          <WaOverlay onClick={closeWhatsApp} />
          <WaPanel>
            <WaPanelHeader>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>WhatsApp Business</div>
                <div style={{ opacity: 0.85, fontSize: '0.85rem' }}>{waProyecto.nombre}</div>
              </div>
              <ActionBtn onClick={closeWhatsApp} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>
                Cerrar
              </ActionBtn>
            </WaPanelHeader>
            <WaPanelBody>
              {waLoading ? (
                <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem 0' }}>Consultando estado...</p>
              ) : waStatus?.connected ? (
                <>
                  <WaStatusCard $connected>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>&#9989;</div>
                    <div style={{ fontWeight: 700, color: '#059669', fontSize: '1.05rem' }}>WhatsApp Conectado</div>
                  </WaStatusCard>

                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '0.3rem' }}>Número</div>
                    <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{waStatus.phone_number || '—'}</div>
                  </div>

                  {waStatus.display_name && (
                    <div style={{ background: '#f8fafc', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '0.3rem' }}>Nombre de display</div>
                      <div style={{ fontWeight: 600 }}>{waStatus.display_name}</div>
                    </div>
                  )}

                  {waStatus.business_name && (
                    <div style={{ background: '#f8fafc', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '0.3rem' }}>Empresa</div>
                      <div style={{ fontWeight: 600 }}>{waStatus.business_name}</div>
                    </div>
                  )}

                  {waStatus.connected_at && (
                    <div style={{ fontSize: '0.82rem', color: '#94a3b8', textAlign: 'center', marginTop: '1rem' }}>
                      Conectado: {waStatus.connected_at}
                    </div>
                  )}

                  <div style={{ textAlign: 'center' }}>
                    <WaDisconnectBtn onClick={handleDisconnectWhatsApp}>
                      Desconectar WhatsApp
                    </WaDisconnectBtn>
                  </div>
                </>
              ) : (
                <>
                  <WaStatusCard $connected={false}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>&#128247;</div>
                    <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '0.5rem' }}>No conectado</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      Conecta tu número de WhatsApp Business para habilitar el entrenamiento por WhatsApp.
                    </div>
                  </WaStatusCard>

                  <WaConnectBtn onClick={handleConnectWhatsApp} disabled={waConnecting}>
                    {waConnecting ? 'Conectando...' : 'Conectar WhatsApp Business'}
                  </WaConnectBtn>

                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#eff6ff', borderRadius: 10, fontSize: '0.83rem', color: '#1e40af' }}>
                    <strong>¿Cómo funciona?</strong>
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
                      <li>Se abrirá una ventana de Meta para autorizar</li>
                      <li>Selecciona tu cuenta de WhatsApp Business</li>
                      <li>El número quedará vinculado a este proyecto</li>
                      <li>Podrás enviar contenido de entrenamiento por WhatsApp</li>
                    </ul>
                  </div>
                </>
              )}
            </WaPanelBody>
          </WaPanel>
        </>
      )}

      {membersProyecto && (
        <>
          <WaOverlay onClick={closeMembers} />
          <WaPanel>
            <WaPanelHeader style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Miembros</div>
                <div style={{ opacity: 0.85, fontSize: '0.85rem' }}>{membersProyecto.nombre}</div>
              </div>
              <ActionBtn onClick={closeMembers} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>
                Cerrar
              </ActionBtn>
            </WaPanelHeader>
            <WaPanelBody>
              {/* Buscar y agregar */}
              <div style={{ marginBottom: '1.5rem' }}>
                <Label style={{ marginBottom: '0.5rem', display: 'block' }}>Agregar miembro</Label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Input
                    placeholder="Buscar por nombre o email..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Select value={addRol} onChange={e => setAddRol(e.target.value)} style={{ width: 130 }}>
                    <option value="supervisor">Supervisor</option>
                    <option value="coordinador">Coordinador</option>
                  </Select>
                </div>
                {searchLoading && <p style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Buscando...</p>}
                {searchResults.length > 0 && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 180, overflowY: 'auto' }}>
                    {searchResults.map(u => (
                      <div key={u.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '0.5rem 0.75rem', borderBottom: '1px solid #f1f5f9',
                        fontSize: '0.88rem'
                      }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{u.nombre}</div>
                          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{u.email}</div>
                        </div>
                        <ActionBtn onClick={() => handleAddMember(u.id)} style={{ fontSize: '0.78rem' }}>
                          + Agregar
                        </ActionBtn>
                      </div>
                    ))}
                  </div>
                )}
                {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                  <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0.5rem 0' }}>Sin resultados</p>
                )}
              </div>

              {/* Lista de miembros actuales */}
              <Label style={{ marginBottom: '0.5rem', display: 'block' }}>
                Miembros actuales ({miembros.length})
              </Label>
              {membersLoading ? (
                <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>Cargando...</p>
              ) : miembros.length === 0 ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '1rem', fontSize: '0.9rem' }}>
                  No hay miembros asignados
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {miembros.map(m => (
                    <div key={m.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '0.6rem 0.75rem', background: '#f8fafc', borderRadius: 8,
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{m.nombre}</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{m.email}</div>
                      </div>
                      <Select
                        value={m.rol_proyecto}
                        onChange={e => handleChangeRole(m.id, e.target.value)}
                        style={{ width: 120, fontSize: '0.82rem', marginRight: '0.5rem' }}
                      >
                        <option value="supervisor">Supervisor</option>
                        <option value="coordinador">Coordinador</option>
                      </Select>
                      <DeleteBtn onClick={() => handleRemoveMember(m.id)} style={{ fontSize: '0.78rem', margin: 0 }}>
                        Eliminar
                      </DeleteBtn>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '1.5rem', padding: '0.75rem', background: '#eff6ff', borderRadius: 8, fontSize: '0.82rem', color: '#1e40af' }}>
                <strong>Roles:</strong>
                <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>
                  <li><strong>Coordinador</strong> — Dashboard completo + conectar/desconectar WhatsApp</li>
                  <li><strong>Supervisor</strong> — Dashboard solo lectura (metricas, progreso)</li>
                </ul>
              </div>
            </WaPanelBody>
          </WaPanel>
        </>
      )}

      {registroProyecto && (
        <>
          <WaOverlay onClick={closeRegistro} />
          <WaPanel style={{ width: 500 }}>
            <WaPanelHeader style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Configuración de Registro</div>
                <div style={{ opacity: 0.85, fontSize: '0.85rem' }}>{registroProyecto.nombre}</div>
              </div>
              <ActionBtn onClick={closeRegistro} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>
                Cerrar
              </ActionBtn>
            </WaPanelHeader>
            <WaPanelBody>
              {registroLoading ? (
                <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem 0' }}>Cargando...</p>
              ) : (
                <>
                  {/* === Convenios CRUD === */}
                  <div style={{ marginBottom: '2rem' }}>
                    <Label style={{ marginBottom: '0.75rem', display: 'block', fontSize: '1rem', fontWeight: 700 }}>
                      Convenios
                    </Label>

                    {convenios.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem' }}>
                        {convenios.map(c => (
                          <div key={c.id} style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.5rem 0.6rem', background: '#f8fafc', borderRadius: 8,
                            border: '1px solid #e2e8f0', fontSize: '0.88rem'
                          }}>
                            {editingConvenio === c.id ? (
                              <>
                                <Input
                                  value={c.nombre}
                                  onChange={e => setConvenios(prev => prev.map(x => x.id === c.id ? { ...x, nombre: e.target.value } : x))}
                                  style={{ flex: 1, fontSize: '0.85rem', padding: '0.3rem 0.5rem' }}
                                  placeholder="Nombre"
                                />
                                <Input
                                  value={c.institucion || ''}
                                  onChange={e => setConvenios(prev => prev.map(x => x.id === c.id ? { ...x, institucion: e.target.value } : x))}
                                  style={{ flex: 1, fontSize: '0.85rem', padding: '0.3rem 0.5rem' }}
                                  placeholder="Institución"
                                />
                                <ActionBtn onClick={() => handleUpdateConvenio(c)} style={{ fontSize: '0.78rem' }}>Guardar</ActionBtn>
                                <ActionBtn onClick={() => setEditingConvenio(null)} style={{ fontSize: '0.78rem' }}>Cancelar</ActionBtn>
                              </>
                            ) : (
                              <>
                                <div style={{ flex: 1 }}>
                                  <strong>{c.nombre}</strong>
                                  {c.institucion && <span style={{ color: '#64748b' }}> — {c.institucion}</span>}
                                </div>
                                <ActionBtn onClick={() => setEditingConvenio(c.id)} style={{ fontSize: '0.78rem' }}>Editar</ActionBtn>
                                <DeleteBtn onClick={() => handleDeleteConvenio(c.id)} style={{ fontSize: '0.78rem', margin: 0 }}>Eliminar</DeleteBtn>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <InlineRow>
                      <Input
                        value={newConvenio.nombre}
                        onChange={e => setNewConvenio(prev => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Nombre del convenio"
                        style={{ flex: 1, fontSize: '0.85rem' }}
                        onKeyDown={e => e.key === 'Enter' && handleAddConvenio()}
                      />
                      <Input
                        value={newConvenio.institucion}
                        onChange={e => setNewConvenio(prev => ({ ...prev, institucion: e.target.value }))}
                        placeholder="Institución (opcional)"
                        style={{ flex: 1, fontSize: '0.85rem' }}
                        onKeyDown={e => e.key === 'Enter' && handleAddConvenio()}
                      />
                      <ActionBtn onClick={handleAddConvenio} style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>+ Agregar</ActionBtn>
                    </InlineRow>
                  </div>

                  {/* === Campos de Registro === */}
                  <div style={{ marginBottom: '2rem' }}>
                    <Label style={{ marginBottom: '0.75rem', display: 'block', fontSize: '1rem', fontWeight: 700 }}>
                      Campos de Registro
                    </Label>

                    {['convenio', 'telefono', 'especialidad', 'pais', 'dni'].map(campo => (
                      <div key={campo} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.6rem 0.75rem', background: '#f8fafc', borderRadius: 8,
                        border: '1px solid #e2e8f0', marginBottom: '0.4rem'
                      }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', textTransform: 'capitalize' }}>
                          {campo === 'telefono' ? 'Teléfono' : campo === 'pais' ? 'País' : campo === 'dni' ? (registroConfig.campos?.dni?.etiqueta || 'DNI') : campo.charAt(0).toUpperCase() + campo.slice(1)}
                        </span>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                          <ToggleSwitch $on={!!registroConfig.campos[campo]?.visible}>
                            <input
                              type="checkbox"
                              checked={!!registroConfig.campos[campo]?.visible}
                              onChange={() => toggleCampo(campo, 'visible')}
                            />
                            <div className="slider" />
                            <span style={{ fontSize: '0.82rem' }}>Visible</span>
                          </ToggleSwitch>
                          <ToggleSwitch $on={!!registroConfig.campos[campo]?.requerido}>
                            <input
                              type="checkbox"
                              checked={!!registroConfig.campos[campo]?.requerido}
                              onChange={() => toggleCampo(campo, 'requerido')}
                            />
                            <div className="slider" />
                            <span style={{ fontSize: '0.82rem' }}>Obligatorio</span>
                          </ToggleSwitch>
                        </div>
                      </div>
                    ))}

                    {/* Etiqueta personalizada para DNI */}
                    {registroConfig.campos?.dni?.visible && (
                      <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.75rem', background: '#fffbeb', borderRadius: 8, border: '1px solid #fbbf24' }}>
                        <Label style={{ fontSize: '0.85rem', marginBottom: '0.3rem', display: 'block' }}>Etiqueta para campo de identificación</Label>
                        <Input
                          value={registroConfig.campos?.dni?.etiqueta || 'DNI'}
                          onChange={e => setRegistroConfig(prev => ({
                            ...prev,
                            campos: { ...prev.campos, dni: { ...prev.campos.dni, etiqueta: e.target.value } }
                          }))}
                          placeholder="ej: Cédula, RUT, CPF, Pasaporte..."
                          style={{ fontSize: '0.85rem', marginBottom: 0 }}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#92400e' }}>Se mostrará este nombre en el formulario de registro</span>
                      </div>
                    )}
                  </div>

                  {/* === Especialidades === */}
                  <div style={{ marginBottom: '2rem' }}>
                    <Label style={{ marginBottom: '0.5rem', display: 'block' }}>Especialidades</Label>
                    <ChipContainer>
                      {registroConfig.especialidades.map(e => (
                        <Chip key={e}>{e}<button onClick={() => removeEspecialidad(e)}>&times;</button></Chip>
                      ))}
                    </ChipContainer>
                    <InlineRow>
                      <Input
                        value={newEspecialidad}
                        onChange={e => setNewEspecialidad(e.target.value)}
                        placeholder="Nueva especialidad"
                        style={{ flex: 1, fontSize: '0.85rem' }}
                        onKeyDown={e => e.key === 'Enter' && addEspecialidad()}
                      />
                      <ActionBtn onClick={addEspecialidad} style={{ fontSize: '0.82rem' }}>+ Agregar</ActionBtn>
                    </InlineRow>
                  </div>

                  {/* === Países === */}
                  <div style={{ marginBottom: '2rem' }}>
                    <Label style={{ marginBottom: '0.5rem', display: 'block' }}>Países</Label>
                    <ChipContainer>
                      {registroConfig.paises.map(p => (
                        <Chip key={p}>{p}<button onClick={() => removePais(p)}>&times;</button></Chip>
                      ))}
                    </ChipContainer>
                    <InlineRow>
                      <Input
                        value={newPais}
                        onChange={e => setNewPais(e.target.value)}
                        placeholder="Nuevo país"
                        style={{ flex: 1, fontSize: '0.85rem' }}
                        onKeyDown={e => e.key === 'Enter' && addPais()}
                      />
                      <ActionBtn onClick={addPais} style={{ fontSize: '0.82rem' }}>+ Agregar</ActionBtn>
                    </InlineRow>
                  </div>

                  {/* === Guardar === */}
                  <SaveBtn onClick={handleSaveRegistroConfig} disabled={registroSaving} style={{ width: '100%' }}>
                    {registroSaving ? 'Guardando...' : 'Guardar Configuración de Registro'}
                  </SaveBtn>
                </>
              )}
            </WaPanelBody>
          </WaPanel>
        </>
      )}

      {showModal && (
        <Modal onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); resetForm(); } }}>
          <ModalCard>
            <ModalTitle>{editing ? 'Editar Proyecto' : 'Nuevo Proyecto'}</ModalTitle>

            <FormGrid>
              <FormGroup>
                <Label>Nombre *</Label>
                <Input
                  value={form.nombre}
                  onChange={e => {
                    const val = e.target.value;
                    setForm(prev => ({
                      ...prev,
                      nombre: val,
                      ...(!editing ? { slug: autoSlug(val) } : {})
                    }));
                  }}
                  placeholder="Hospital XYZ"
                />
              </FormGroup>

              <FormGroup>
                <Label>Slug * (URL)</Label>
                <Input
                  value={form.slug}
                  onChange={e => setForm(prev => ({ ...prev, slug: autoSlug(e.target.value) }))}
                  placeholder="hospital-xyz"
                />
                {form.slug && (
                  <span style={{ fontSize: '0.78rem', color: '#14b6cb' }}>
                    https://{form.slug}.ateneomentoria.com
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Dominio personalizado (opcional)</Label>
                <Input
                  value={form.dominio_personalizado}
                  onChange={e => setForm(prev => ({ ...prev, dominio_personalizado: e.target.value }))}
                  placeholder="educacion.hospital.com"
                />
              </FormGroup>

              <FormGroup>
                <Label>Rol Default (registro)</Label>
                <Select
                  value={form.rol_default}
                  onChange={e => setForm(prev => ({ ...prev, rol_default: e.target.value }))}
                >
                  <option value="">Sin rol automático</option>
                  {groups.map(g => (
                    <option key={g.id || g.name} value={g.name}>{g.name}{g.description ? ` — ${g.description}` : ''}</option>
                  ))}
                  <option value="__nuevo__">+ Crear nuevo grupo...</option>
                </Select>
                {form.rol_default === '__nuevo__' && (
                  <Input
                    style={{ marginTop: 4 }}
                    placeholder="Nombre del nuevo grupo"
                    onChange={e => setForm(prev => ({ ...prev, rol_default: e.target.value }))}
                    autoFocus
                  />
                )}
              </FormGroup>

              <FormGroup>
                <Label>Color Primario</Label>
                <ColorRow>
                  <ColorInput
                    type="color"
                    value={form.color_primario}
                    onChange={e => setForm(prev => ({ ...prev, color_primario: e.target.value }))}
                  />
                  <Input
                    value={form.color_primario}
                    onChange={e => setForm(prev => ({ ...prev, color_primario: e.target.value }))}
                    style={{ width: 100 }}
                  />
                </ColorRow>
              </FormGroup>

              <FormGroup>
                <Label>Color Secundario</Label>
                <ColorRow>
                  <ColorInput
                    type="color"
                    value={form.color_secundario}
                    onChange={e => setForm(prev => ({ ...prev, color_secundario: e.target.value }))}
                  />
                  <Input
                    value={form.color_secundario}
                    onChange={e => setForm(prev => ({ ...prev, color_secundario: e.target.value }))}
                    style={{ width: 100 }}
                  />
                </ColorRow>
              </FormGroup>

              <FormGroup $full>
                <Label>Logo del proyecto</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <Input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files[0])} />
                  {editing?.logo && !logoFile && (
                    <LogoPreview src={`${BACKEND_BASE}/${editing.logo}`} alt="Logo actual" />
                  )}
                </div>
              </FormGroup>

              <FormGroup $full>
                <Label>Título de Landing</Label>
                <Input
                  value={form.titulo_landing}
                  onChange={e => setForm(prev => ({ ...prev, titulo_landing: e.target.value }))}
                  placeholder="Nombre que aparece en la landing (default: MentorIA)"
                />
              </FormGroup>

              <FormGroup $full>
                <Label>Subtítulo de Landing</Label>
                <TextArea
                  value={form.subtitulo_landing}
                  onChange={e => setForm(prev => ({ ...prev, subtitulo_landing: e.target.value }))}
                  placeholder="Descripción que aparece bajo el título (default: texto MentorIA)"
                />
              </FormGroup>

              <FormGroup $full>
                <Label>Contenidos Asignados</Label>
                <CheckboxList>
                  {documentos.length === 0 ? (
                    <span style={{ color: '#94a3b8' }}>No hay contenidos disponibles</span>
                  ) : documentos.map(doc => (
                    <CheckboxItem key={doc.id}>
                      <input
                        type="checkbox"
                        checked={form.documento_ids.includes(doc.id) || form.documento_ids.includes(String(doc.id))}
                        onChange={() => toggleDoc(doc.id)}
                      />
                      {doc.titulo || doc.nombre || `Doc #${doc.id}`}
                    </CheckboxItem>
                  ))}
                </CheckboxList>
              </FormGroup>

              <FormGroup $full>
                <Label>Modos de Aprendizaje</Label>
                <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                  {[
                    { key: 'consulta', label: 'Consulta' },
                    { key: 'mentor', label: 'Mentor' },
                    { key: 'evaluacion', label: 'Evaluación' },
                    { key: 'reto', label: 'Reto Semanal' },
                    { key: 'practica', label: 'Práctica' }
                  ].map(m => (
                    <ToggleSwitch key={m.key} $on={!!form.modos_habilitados[m.key]}>
                      <input
                        type="checkbox"
                        checked={!!form.modos_habilitados[m.key]}
                        onChange={e => setForm(prev => ({
                          ...prev,
                          modos_habilitados: { ...prev.modos_habilitados, [m.key]: e.target.checked }
                        }))}
                      />
                      <div className="slider" />
                      {m.label}
                    </ToggleSwitch>
                  ))}
                </div>
              </FormGroup>

              <FormGroup>
                <ToggleSwitch $on={!!form.registro_abierto}>
                  <input
                    type="checkbox"
                    checked={!!form.registro_abierto}
                    onChange={e => setForm(prev => ({ ...prev, registro_abierto: e.target.checked ? 1 : 0 }))}
                  />
                  <div className="slider" />
                  Registro abierto
                </ToggleSwitch>
              </FormGroup>

              {editing && (
                <FormGroup>
                  <ToggleSwitch $on={!!form.activo}>
                    <input
                      type="checkbox"
                      checked={!!form.activo}
                      onChange={e => setForm(prev => ({ ...prev, activo: e.target.checked ? 1 : 0 }))}
                    />
                    <div className="slider" />
                    Proyecto activo
                  </ToggleSwitch>
                </FormGroup>
              )}
            </FormGrid>

            <BtnRow>
              <CancelBtn onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</CancelBtn>
              <SaveBtn onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear Proyecto')}
              </SaveBtn>
            </BtnRow>
          </ModalCard>
        </Modal>
      )}
    </Container>
  );
};

export default AdminProyectos;
